import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { borderRadius, colors, spacing, Text } from '@retailer-platform/shared-components'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { type HeroBannerContentTextValues } from 'domains/merchandising/types/heroBanner.types'
import { useDashMessages } from 'intl/intl.hooks'

type FieldProps = {
  color: string
  name: string
  value: string
}

const sectionContainer = css({
  display: 'flex',
  flexDirection: 'column',
})

const rowContainer = css({
  display: 'flex',
  marginBottom: spacing.X24,
  width: '100%',
})

const textContainer = css({
  width: '280px',
})

const colorBlockStyle = css({
  background: colors.GRAYSCALE.X70,
  borderRadius: borderRadius.X4,
  display: 'inline-block',
  height: '16px',
  marginRight: spacing.X8,
  width: '16px',
  verticalAlign: 'middle',
})

const placeholderImageStyle = css({
  border: `1px dashed ${colors.GRAYSCALE.X30}`,
  marginTop: spacing.X8,
  marginLeft: spacing.X8,
  width: '250px',
})

const imageStyle = css({
  width: '100%',
  display: 'block',
})

export const HeroBannerDetailsContentText: FunctionComponent<HeroBannerContentTextValues> = ({
  titleSuffix,
  button,
  descriptionText,
  headingText,
  subheadingText,
  imageAltText,
  images,
  backgroundColor,
}) => {
  const messages = useDashMessages({
    altTextLabel: 'merchandising.hero-banners.section.content.field.altText.label',
    colorLabel: 'merchandising.hero-banners.details.color.label',
    emptyValue: 'merchandising.hero-banners.field.empty.value',
    imageLabel: 'merchandising.hero-banners.section.content.field.image.label',
  })

  const fields: FieldProps[] = [
    {
      name: 'Heading',
      color: headingText.color || messages.emptyValue,
      value: headingText.text || messages.emptyValue,
    },
    {
      name: 'Subheading',
      color: subheadingText.color || messages.emptyValue,
      value: subheadingText.text || messages.emptyValue,
    },
    {
      name: 'Description',
      color: descriptionText.color || messages.emptyValue,
      value: descriptionText.text || messages.emptyValue,
    },
    {
      name: 'CTA',
      color: colors?.[button?.styleVariant]?.REGULAR || messages.emptyValue,
      value: button.label || messages.emptyValue,
    },
    {
      name: 'Background',
      color: backgroundColor || messages.emptyValue,
      value: null,
    },
  ]

  return (
    <MerchandisingFormSection
      titleId="merchandising.hero-banners.section.content.title"
      titleSuffix={titleSuffix}
    >
      <div css={sectionContainer}>
        {fields.map(({ name, color, value }) => (
          <div css={rowContainer} key={`herobanner-details-info-${name}`}>
            {value && (
              <div css={textContainer}>
                <Text weight="semibold">{name}</Text>
                <Text size="medium">{value}</Text>
              </div>
            )}
            <div>
              <Text weight="semibold">
                {name} {messages.colorLabel}
              </Text>
              <Text size="medium">
                <span css={[colorBlockStyle, { backgroundColor: color }]} />
                {color}
              </Text>
            </div>
          </div>
        ))}
        <div css={rowContainer} key="herobanner-details-info-image">
          <div css={textContainer}>
            <Text weight="semibold">{messages.imageLabel}</Text>
            <div css={placeholderImageStyle}>
              {images?.large && <img css={imageStyle} src={images.large} />}
            </div>
          </div>
        </div>
        <div css={rowContainer} key="herobanner-details-info-alttext">
          <div css={textContainer}>
            <Text weight="semibold">{messages.altTextLabel}</Text>
            <Text size="medium">{imageAltText || messages.emptyValue}</Text>
          </div>
        </div>
      </div>
    </MerchandisingFormSection>
  )
}
